import axios from 'axios';
import toSnakeCase from 'to-snake-case';
import _ from 'lodash';

import * as Constants from './constants';


export const xapi = (fileUpload = false) => {
  let headers = {
    'X-Requested-With': 'XMLHttpRequest'
  };

  if (fileUpload) {
    headers = {
      ...headers,
      'Content-Type': 'multipart/form-data'
    }
  }

  return axios.create({
    baseURL: Constants.BASE_URL,
    headers: {
      ...headers,
      Accept: 'application/json'
    }
  });
};

export const snakeCaseKeys = obj => {
  let snake_case_obj;

  if (obj instanceof Array) {
    return obj.map(element => {
      let newObj = element;
      if (typeof element === 'object') {
        newObj = snakeCaseKeys(element);
      }
      return newObj;
    });
  } else {
    snake_case_obj = {};

    for (const objKey in obj) {
      if (obj.hasOwnProperty(objKey)) {
        const newObjKey = toSnakeCase(objKey);
        let keyValue = obj[objKey];

        if (
            keyValue instanceof Array ||
            (keyValue !== null && keyValue !== undefined && keyValue.constructor === Object)
        ) {
          keyValue = snakeCaseKeys(keyValue);
        }
        snake_case_obj[newObjKey] = keyValue;
      }
    }
  }

  return snake_case_obj;
};

export const convertObjectKeyToCamelCase = obj => {
  let camelCaseObj;

  if (obj instanceof Array) {
    return obj.map(element => {
      let newObj = element;
      if (typeof element === 'object') {
        newObj = convertObjectKeyToCamelCase(element);
      }
      return newObj;
    });
  } else {
    camelCaseObj = {};

    for (const objKey in obj) {
      if (obj.hasOwnProperty(objKey)) {
        const newObjKey = _.camelCase(objKey);
        let keyValue = obj[objKey];

        if (
            keyValue instanceof Array ||
            (keyValue !== null && keyValue !== undefined && keyValue.constructor === Object)
        ) {
          keyValue = convertObjectKeyToCamelCase(keyValue);
        }
        camelCaseObj[newObjKey] = keyValue;
      }
    }
  }

  return camelCaseObj;
};


// ─── WhatConverts Tracking ────────────────────────────────────────────────────

/**
 * Fields to exclude from WhatConverts tracking.
 * These are file uploads, signatures, internal flags, or sensitive data
 * that should not be sent to a third-party analytics service.
 */
const EXCLUDED_FIELDS = new Set([
  'pdf',
  'coverageFile',
  'actContactId',
  'appStatus',
  'status',
]);

/**
 * Flattens a nested form state object into a flat key→value map
 * suitable for WhatConverts field tracking.
 *
 * - Skips null, undefined, empty strings, and excluded fields
 * - Converts arrays to comma-separated strings
 * - Recursively flattens nested objects with dot-notation keys (e.g. "customer.email")
 * - Converts camelCase keys to human-readable "Title Case" labels
 *
 * @param {Object} obj        - The form state object (e.g. state.quote)
 * @param {string} [prefix]   - Key prefix for nested objects (used in recursion)
 * @returns {Object}          - Flat { 'Label': 'value' } map
 */
export function flattenFormData(obj, prefix = '') {
  const result = {};

  for (const key in obj) {
    if (!obj.hasOwnProperty(key)) continue;
    if (EXCLUDED_FIELDS.has(key)) continue;

    const value = obj[key];
    const fullKey = prefix ? `${prefix}.${key}` : key;

    // Skip empty/null/undefined
    if (value === null || value === undefined || value === '') continue;

    // Skip File objects
    if (value instanceof File || value instanceof Blob) continue;

    if (Array.isArray(value)) {
      // Convert arrays to comma-separated string
      const str = value.filter(v => v !== null && v !== undefined && v !== '').join(', ');
      if (str) result[toReadableLabel(fullKey)] = str;

    } else if (typeof value === 'object' && value.constructor === Object) {
      // Recursively flatten nested objects
      const nested = flattenFormData(value, fullKey);
      Object.assign(result, nested);

    } else {
      // Primitive value
      result[toReadableLabel(fullKey)] = String(value);
    }
  }

  return result;
}

/**
 * Converts a camelCase or dot-notation key into a human-readable label.
 * e.g. "customer.zipCode" → "Customer Zip Code"
 *
 * @param {string} key
 * @returns {string}
 */
function toReadableLabel(key) {
  return key
      .split('.')
      .map(part =>
          part
              // Insert space before uppercase letters
              .replace(/([A-Z])/g, ' $1')
              // Capitalize first letter
              .replace(/^./, str => str.toUpperCase())
              .trim()
      )
      .join(' - ');
}


/**
 * Sends full form data to WhatConverts for lead tracking.
 *
 * HOW TO USE — call this inside each module's `createCustomer` action
 * (inside the `.then()` callback, after the customer is created):
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  // homeInspector module → createCustomer action:
 *  trackWhatConvertsLead(state.quote.customer, FORM_IDS.HOME_INSPECTOR, state.quote);
 *
 *  // individualAppraiser module → createCustomer action:
 *  trackWhatConvertsLead(state.quote.customer, FORM_IDS.INDIVIDUAL_APPRAISER, state.quote);
 *
 *  // groupAppraiser module → createCustomer action:
 *  trackWhatConvertsLead(state.quote.customer, FORM_IDS.GROUP_APPRAISER, state.quote);
 *
 *  // standardApplication module → createCustomer action:
 *  trackWhatConvertsLead(state.quote.customer, FORM_IDS.STANDARD_APPLICATION, state.quote);
 *
 *  // rxApplication module → createCustomer action:
 *  trackWhatConvertsLead(state.quote.customer, FORM_IDS.RX_APPLICATION, state.quote);
 *
 *  // axApplication module → createCustomer action:
 *  trackWhatConvertsLead(state.quote.customer, FORM_IDS.AX_APPLICATION, state.quote);
 *
 *  // ipfs module → createCustomer action:
 *  trackWhatConvertsLead(state.quote.customer, FORM_IDS.IPFS, state.quote);
 *
 *  // piApplication module → createCustomer action:
 *  trackWhatConvertsLead(state.quote.customer, FORM_IDS.PI_APPLICATION, state.quote);
 *
 *  // sipApplication module → createCustomer action:
 *  trackWhatConvertsLead(state.quote.customer, FORM_IDS.SIP_APPLICATION, state.quote);
 *
 *  // ppiApplication module → createCustomer action:
 *  trackWhatConvertsLead(state.quote.customer, FORM_IDS.PPI_APPLICATION, state.quote);
 *
 *  // quickQuote module → createCustomer action:
 *  trackWhatConvertsLead(state.quote.customer, FORM_IDS.QUICK_QUOTE, state.quote);
 *
 *  // rcApplication module → createCustomer action:
 *  trackWhatConvertsLead(state.quote.customer, FORM_IDS.RC_APPLICATION, state.quote);
 *
 *  // insuranceAgentApplication module → createCustomer action:
 *  trackWhatConvertsLead(state.quote.customer, FORM_IDS.INSURANCE_AGENT_APPLICATION, state.quote);
 *
 *  // mgaApplication module → createCustomer action:
 *  trackWhatConvertsLead(state.mgaApplication.customer, FORM_IDS.MGA_APPLICATION, state.mgaApplication);
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * @param {Object} customer   - The customer object from state (for core lead fields)
 * @param {number} formId     - WhatConverts form ID from FORM_IDS constant
 * @param {Object} [formData] - Full form state (state.quote or state.mgaApplication etc.)
 */
export function trackWhatConvertsLead(customer, formId, formData = {}) {
  if (!window.$wc_leads || !window.$wc_leads.track) return;

  // Core customer fields — always sent as top-level named fields
  const customerFields = {
    'Name':    customer.contactName  || '',
    'Company': customer.companyName  || '',
    'Email':   customer.email        || '',
    'Phone':   customer.phone        || '',
    'Address': customer.address      || '',
    'City':    customer.city         || '',
    'State':   customer.state        || '',
    'Zip':     customer.zipCode      || '',
  };

  // Flatten all remaining form fields (excluding the customer sub-object
  // since we already captured it above, and excluded sensitive fields)
  const { customer: _customer, ...restOfForm } = formData;
  const extraFields = flattenFormData(restOfForm);

  window.$wc_leads.track.form(formId, {
    ...customerFields,
    ...extraFields,
  });
}